/**
 * OnboardingTour
 *
 * First-run dashboard walkthrough — 5 steps covering the core tabs.
 *
 * Persistence strategy:
 *   1. Fast-path: localStorage key `ss_tour_v1_done` prevents any flash on
 *      subsequent logins on the same device.
 *   2. Source of truth: profiles.tour_done in Supabase — ensures the tour
 *      never re-appears after the user dismisses it, even on a new device
 *      or after clearing browser storage.
 *
 * Mobile behaviour:
 *   - Cards always center on viewports < 640 px wide (no anchor positioning).
 *   - Card width fills the screen with 16 px gutters.
 *   - Touch targets are at least 44 px tall.
 */

import React, { useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '@/components/ui/button';
import { X } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

const LS_KEY = 'ss_tour_v1_done';

interface Step {
  title: string;
  body: string;
  target?: string;           // matches [data-tour="…"] attribute
  align: 'center' | 'top-left' | 'top-right';
  icon: string;
}

const STEPS: Step[] = [
  {
    icon: '👋',
    title: 'Welcome to SocialScoop.',
    body: 'Your feed — owned by you, not an algorithm. This quick tour walks you through the four things that make it work.',
    align: 'center',
  },
  {
    icon: '📡',
    title: 'Connect your platforms.',
    body: 'Head to Platforms and link Reddit, Bluesky, YouTube, or X. SocialScoop fetches new content every 20 minutes so it\'s ready when you arrive.',
    target: 'platforms',
    align: 'top-left',
  },
  {
    icon: '📋',
    title: 'Set your Listening Rules.',
    body: 'Write what you want in plain English — "Show me posts about climate science" or "Prioritize r/science threads." Use the Intensity Gauge to tune, not just toggle.',
    target: 'rules',
    align: 'top-left',
  },
  {
    icon: '🧲',
    title: 'Your Feeds tab.',
    body: 'Everything that passed your rules shows up here, already curated. Content you\'ve fenced moves quietly to the Vault — out of the way, never deleted.',
    target: 'feeds',
    align: 'top-left',
  },
  {
    icon: '✨',
    title: 'Follow a curator.',
    body: 'SocialScoop\'s founding curators have public feeds you can follow right now — find them on the Home tab. Your own feed can go public too.',
    align: 'center',
  },
];

// ─── helpers ────────────────────────────────────────────────────────────────

function lsGet(key: string): string | null {
  try { return localStorage.getItem(key); } catch { return null; }
}
function lsSet(key: string, value: string): void {
  try { localStorage.setItem(key, value); } catch { /* ignore */ }
}

const isMobile = () => (typeof window !== 'undefined' ? window.innerWidth < 640 : false);

// ─── Card component ──────────────────────────────────────────────────────────

interface TourCardProps {
  step: Step;
  index: number;
  total: number;
  onNext: () => void;
  onSkip: () => void;
  targetRect: DOMRect | null;
}

const TourCard: React.FC<TourCardProps> = ({ step, index, total, onNext, onSkip, targetRect }) => {
  const isLast   = index === total - 1;
  const mobile   = isMobile();
  const hasAnchor = targetRect && step.align !== 'center' && !mobile;

  const getStyle = (): React.CSSProperties => {
    if (!hasAnchor) {
      return {
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: mobile ? `calc(100vw - 32px)` : '400px',
        maxWidth: mobile ? 'calc(100vw - 32px)' : '400px',
      };
    }

    const gap   = 12;
    const cardW = 360;

    if (step.align === 'top-left') {
      return {
        position: 'fixed',
        top: Math.min(targetRect!.bottom + gap, window.innerHeight - 320),
        left: Math.max(targetRect!.left, 12),
        width: `${cardW}px`,
        maxWidth: `calc(100vw - 24px)`,
      };
    }

    if (step.align === 'top-right') {
      return {
        position: 'fixed',
        top: Math.min(targetRect!.bottom + gap, window.innerHeight - 320),
        right: Math.max(window.innerWidth - targetRect!.right, 12),
        width: `${cardW}px`,
        maxWidth: `calc(100vw - 24px)`,
      };
    }

    return {
      position: 'fixed',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      width: '400px',
      maxWidth: 'calc(100vw - 32px)',
    };
  };

  return (
    <div
      style={{ ...getStyle(), zIndex: 10001 }}
      className="bg-background border border-border rounded-2xl shadow-2xl animate-in fade-in slide-in-from-bottom-2 duration-300"
    >
      {/* Inner padding — generous on mobile */}
      <div className="px-6 py-6 sm:px-8 sm:py-7">
        {/* Close button */}
        <button
          onClick={onSkip}
          className="absolute top-4 right-4 p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
          aria-label="Skip tour"
          style={{ minWidth: 36, minHeight: 36 }}
        >
          <X className="h-4 w-4" />
        </button>

        {/* Icon + counter */}
        <div className="flex items-center gap-3 mb-4">
          <span className="text-2xl sm:text-3xl leading-none">{step.icon}</span>
          <span className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
            {index + 1} of {total}
          </span>
        </div>

        <h2 className="text-lg sm:text-xl font-semibold tracking-tight mb-2 pr-6">
          {step.title}
        </h2>
        <p className="text-sm sm:text-base text-muted-foreground leading-relaxed mb-6">
          {step.body}
        </p>

        {/* Progress dots */}
        <div className="flex items-center gap-1.5 mb-5">
          {STEPS.map((_, i) => (
            <div
              key={i}
              className={`h-1.5 rounded-full transition-all duration-300 ${
                i === index ? 'w-5 bg-foreground' : 'w-1.5 bg-border'
              }`}
            />
          ))}
        </div>

        {/* Actions */}
        <div className="flex items-center justify-between gap-3">
          <button
            onClick={onSkip}
            className="text-xs text-muted-foreground hover:text-foreground transition-colors underline-offset-4 hover:underline py-2"
            style={{ minHeight: 44 }}
          >
            Skip tour
          </button>
          <Button
            size="sm"
            onClick={onNext}
            className="px-6 h-11 text-sm"
          >
            {isLast ? 'Get started →' : 'Next →'}
          </Button>
        </div>
      </div>
    </div>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────

export const OnboardingTour: React.FC = () => {
  const { user } = useAuth();
  const [step,       setStep]       = useState(0);
  const [visible,    setVisible]    = useState(false);
  const [targetRect, setTargetRect] = useState<DOMRect | null>(null);
  const [checked,    setChecked]    = useState(false); // have we finished the async check?

  // ── Determine whether to show the tour ─────────────────────────────────────
  useEffect(() => {
    let cancelled = false;

    async function check() {
      // 1. Fast-path: localStorage says we're done → don't show
      if (lsGet(LS_KEY) === '1') {
        setChecked(true);
        return;
      }

      // 2. If there's no logged-in user, skip (anonymous / loading state)
      if (!user) {
        setChecked(true);
        return;
      }

      // 3. Check Supabase — this is the cross-device source of truth
      const { data } = await supabase
        .from('profiles')
        .select('tour_done')
        .eq('id', user.id)
        .maybeSingle();

      if (cancelled) return;

      if (data?.tour_done) {
        // Sync the fast-path so future page loads skip the DB call
        lsSet(LS_KEY, '1');
        setChecked(true);
        return;
      }

      // Not done — show the tour after a short delay for the dashboard to paint
      setChecked(true);
      const timer = setTimeout(() => {
        if (!cancelled) setVisible(true);
      }, 900);
      return () => clearTimeout(timer);
    }

    check();
    return () => { cancelled = true; };
  }, [user]);

  // ── Measure target element for spotlight ──────────────────────────────────
  const measureTarget = useCallback((target?: string) => {
    if (!target || isMobile()) { setTargetRect(null); return; }
    const el = document.querySelector(`[data-tour="${target}"]`);
    setTargetRect(el ? el.getBoundingClientRect() : null);
  }, []);

  useEffect(() => {
    if (visible) measureTarget(STEPS[step]?.target);
  }, [step, visible, measureTarget]);

  // Re-measure on resize (orientation change on mobile)
  useEffect(() => {
    if (!visible) return;
    const handler = () => measureTarget(STEPS[step]?.target);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, [visible, step, measureTarget]);

  // ── Dismiss — write to both localStorage and Supabase ─────────────────────
  const dismiss = useCallback(async () => {
    setVisible(false);
    lsSet(LS_KEY, '1');

    if (user) {
      await supabase
        .from('profiles')
        .update({ tour_done: true })
        .eq('id', user.id);
    }
  }, [user]);

  const next = useCallback(() => {
    if (step < STEPS.length - 1) {
      setStep((s) => s + 1);
    } else {
      dismiss();
    }
  }, [step, dismiss]);

  if (!checked || !visible) return null;

  const currentStep = STEPS[step];

  return createPortal(
    <>
      {/* Backdrop */}
      <div
        style={{ zIndex: 10000 }}
        className="fixed inset-0 bg-black/50 backdrop-blur-[2px] transition-opacity duration-300"
        onClick={dismiss}
      />

      {/* Spotlight ring — only when we have a target and not on mobile */}
      {targetRect && !isMobile() && (
        <div
          style={{
            position: 'fixed',
            top:    targetRect.top    - 4,
            left:   targetRect.left   - 4,
            width:  targetRect.width  + 8,
            height: targetRect.height + 8,
            zIndex: 10000,
            borderRadius: 8,
            boxShadow: '0 0 0 4px rgba(255,255,255,0.9), 0 0 0 8px rgba(255,255,255,0.2)',
            pointerEvents: 'none',
          }}
        />
      )}

      <TourCard
        step={currentStep}
        index={step}
        total={STEPS.length}
        onNext={next}
        onSkip={dismiss}
        targetRect={targetRect}
      />
    </>,
    document.body,
  );
};

export default OnboardingTour;
